import { Navigate, Outlet, useLocation } from "react-router-dom";
import { Spinner } from "../../components";
import { useFeatures } from "../../api/features";
import { useSession } from "../../api/session";

/**
 * §B1.3: "a `RequireSession` route element redirects to `/login` on a 401 from any query."
 *
 * The 401 does not have to come from this component: `client.ts` notifies `SessionProvider` on **every** 401
 * (§B1.1), the provider clears the session, and this element re-renders anonymous. That is why there is no error
 * handling here — the session state is the single source of truth, and a screen deep in the tree that 401s sends
 * the whole app to `/login` without knowing this component exists.
 *
 * P1a/P1-web: the redirect target itself now depends on the deployment. `/login` (the one-field operator key
 * screen) is the fallback every deployment can still use; `/auth/login` (email + password) is where a
 * multiuser deployment wants an anonymous visitor to land instead. `useFeatures()` defaults to
 * `multiuser_enabled: false` before the boot fetch resolves and for any test that renders this component with
 * no `<FeaturesProvider>` in the tree, so the fallback is also the safe default.
 *
 * Fable #8 fix: this element used to decide its redirect target as soon as `session.status` left
 * `"loading"`, without waiting for `features.loading` to resolve too. On a cold load, `GET /api/v1/session`
 * (a single round trip) routinely settles before `GET /api/v1/meta/features` does, so a multiuser deployment
 * with no operator key configured sent a fresh, anonymous visitor to `/login` -- the one-field operator-key
 * form -- while `features.multiuser_enabled` still held its conservative `false` default. That screen has no
 * way to succeed for that visitor and no link off it, so they were stranded. Waiting for BOTH loading flags
 * before choosing a target closes the gap: the spinner shows a beat longer on a slow features fetch, which is
 * a strictly better outcome than a redirect to a form the visitor cannot use.
 *
 * xmg-sec-mfa: an authenticated session that still owes its second factor (`mfa` is `required` or
 * `enrollment_required`, from login, `GET /api/v1/session` or any 403 that names it) goes to `/auth/mfa` or
 * `/auth/mfa/setup` instead of the screen it asked for, which is remembered in `state.from`.
 *
 * xmg-mfa-switch (`LUCIMIG_MFA_ENFORCED`): when `GET /api/v1/meta/features` says `mfa_enforced: false` an account
 * with no factor owes nothing, so `enrollment_required` never forces the set-up screen (the server already
 * answers `verified` in that mode; this guards a stale state). `required` - an account that HAS enrolled - still
 * goes to the code screen: it is asked for its code at sign-in whatever the switch says.
 */
export function RequireSession(): JSX.Element {
  const { status, mfa } = useSession();
  const { features, loading: featuresLoading } = useFeatures();
  const location = useLocation();
  if (status === "loading" || featuresLoading) {
    return <Spinner />;
  }
  if (status === "anonymous") {
    const target = features.multiuser_enabled ? "/auth/login" : "/login";
    return <Navigate to={target} replace state={{ from: `${location.pathname}${location.search}` }} />;
  }
  // xmg-sec-mfa (S3a): a password-only session is not signed in yet. Every API call it makes answers 403, so
  // nothing behind this element can render usefully; send it to the second-factor screen it still owes.
  const enrolmentForced = mfa === "enrollment_required" && features.mfa_enforced !== false;
  if (mfa === "required" || enrolmentForced) {
    const target = mfa === "required" ? "/auth/mfa" : "/auth/mfa/setup";
    return <Navigate to={target} replace state={{ from: `${location.pathname}${location.search}` }} />;
  }
  return <Outlet />;
}
